import { ComponentBase } from "../core/ComponentBase.js";
import { RpcDataProvider } from "../core/RpcDataProvider.js";
import { LocalDataProvider } from "../core/LocalDataProvider.js";

export class DataCrud extends ComponentBase {

    static styles = `
    .data-crud {
        display: flex;
        flex-direction: column;
        background-color: var(--bs-body-bg);
        position: relative;
    }

    .data-crud.custom-height {
        height: auto;
    }

    .data-crud .data-crud-content {
        display: flex;
        flex: 1;
        min-height: 0;
        overflow: hidden;
        position: relative;
    }

    .data-crud .view-container {
        flex: 1;
        min-width: 0;
        height: 100%;
        transition: all var(--ab-t-slow);
    }

    .data-crud .form-container {
        width: 420px;
        height: 100%;
        background-color: var(--bs-body-bg);
        border-left: 1px solid var(--bs-border-color);
        box-shadow: var(--ab-shadow-md);
        transition: transform var(--ab-t-slow),
                    opacity var(--ab-t-slow),
                    width var(--ab-t-slow),
                    border-color var(--ab-t-slow);
        transform: translateX(0);
        opacity: 1;
        z-index: var(--ab-z-sidebar);
        overflow: hidden;
    }

    .data-crud .form-container.hidden {
        transform: translateX(100%);
        opacity: 0;
        width: 0;
        border-left: 0 solid transparent;
        box-shadow: none;
        pointer-events: none;
    }

    @media (max-width: 768px) {
        .data-crud .data-crud-content {
            flex-direction: column;
        }

        .data-crud .view-container {
            flex: 1;
            min-height: 0;
            height: auto;
        }
    }
`;
    constructor(config) {
        const mergedConfig = {
            ...DataCrud.getDefaultConfig(),
            ...config,
        };
        super(mergedConfig);

        // Core components
        this.dataProvider = null;
        this.dataView = null;
        this.dataForm = null;

        // State management
        this.schema = null;
        this.initialized = false;

        this.containerId = `data-crud-${this.eventGroup}`;
        this.viewContainerId = `${this.containerId}-view`;
        this.formContainerId = `${this.containerId}-form`;
    }


    static getDefaultConfig() {
        return {
            // Basic configuration
            eventGroup: null,
            rpcClass: null,
            rpcMethod: null, // Default is null, handled in initializeDataProvider
            schemaPath: null,

            // Layout configuration
            layout: 'standard',
            layoutConfig: {
                formWidth: '400px'
            },

            idField: '_id',
            height: null,

            // View configuration
            viewConfig: {
                mode: 'grid',
                fields: [],
                actions: [],
                filter: {
                    enabled: false,
                    schema: null,
                    fields: {},
                    sections: null
                },
                emptyState: {
                    icon: 'bi bi-collection',
                    message: 'No items found',
                    description: 'Create a new item to get started',
                    action: null
                },
                selection: {
                    enabled: true,
                    multiple: false
                },
                autoLoad: false,
                defaultActions: {
                    edit: true,
                    delete: true,
                },
            },

            // Form configuration
            formConfig: {
                mode: 'display',
                fields: {},
                sections: null,
                actions: {
                    save: true,
                    cancel: true
                },
                validation: {
                    validateOnChange: true,
                    showErrors: true
                }
            },

            dataProviderType: 'rpc', // 'rpc' (default) or 'local'
            // Data configuration
            dataConfig: {
                autoLoad: true,
                pageSize: 25,
                context: null,
                resolveReferences: true
            },

            // Default values for new items
            defaultValues: {},

            // Event handlers
            onError: null
        };
    }

    async initialize() {
        if (this.initialized) return;

        try {

            if (this.config.rdsSubscriptionEnabled !== false) {
                let dataSetName = this.config.rdsDataSetName;
                if (!dataSetName && this.config.schemaPath) {
                    const parts = this.config.schemaPath.split('/');
                    dataSetName = parts[parts.length - 1];
                }
                if (dataSetName) {
                    this.config.rdsSubscriptions = [dataSetName];
                }
            }

            // Now call the parent initialize, which will set up the listeners
            await super.initialize();

            // Load schema
            this.schema = await this.loadSchema();

            // Initialize data provider
            await this.initializeDataProvider();

            // Initialize DataView
            await this.initializeDataView();

            // Set up event handlers
            this.setupEventHandlers();

            // Apply styles
            this.applyStyles();

            // Load initial data if configured
            if (this.config.dataConfig.autoLoad) {
                await this.loadInitialData();
            }

            this.initialized = true;

        } catch (error) {
            this.handleError(error);
            throw error;
        }
    }

    setupEventHandlers() {
        // viewConfig.disableDefaultCreate: true  — skip built-in form on create (use custom navigation)
        // viewConfig.disableDefaultEdit: true    — skip built-in form on edit  (use custom navigation)
        // viewConfig.disableDefaultView: true    — skip built-in form on item click
        const vc = this.config.viewConfig || {};
        if (!vc.disableDefaultCreate) {
            this.dataView.on(`createItem`, () => this.handleCreateItem());
        }
        if (!vc.disableDefaultEdit) {
            this.dataView.on(`editItem`, (item) => this.handleEditItem(item));
        }
        this.dataView.on(`deleteItem`, (item) => this.handleDeleteItem(item));
        if (!vc.disableDefaultView) {
            this.dataView.on(`itemSelected`, (item) => this.handleItemSelected(item));
        }

        // DataProvider events
        this.dataProvider.on(`dataError`, (error) => this.handleDataError(error));
    }


    handleCreateItem() {
        const defaultData = this.getDefaultValues();
        this._showForm("edit", defaultData);
    }

    handleEditItem(item) {
        this._showForm("edit", item);
    }

    handleItemSelected(item) {
        this._showForm("display", item);
    }


    async handleDeleteItem(item) {

        this.appClient.notifier
            .confirm("Are you sure you want to delete this item?", {
                title: "Confirm Delete",
                type: "danger",
                okButtonText: "Delete",
                cancelButtonText: "Cancel",
            })
            .then(async (confirmed) => {
                if (confirmed) {
                    try {
                        await this.dataProvider.delete(item);
                        await this.refreshData();
                    } catch (error) {
                        this.handleDataError(error);
                    }
                }
            });
    }

    handleDataSaved() {
        this.hideForm();
        this.refreshData();
    }

    handleDataUpdated() {
        this.hideForm();
        this.refreshData();
    }

    handleFormCancel() {
        this.hideForm();
    }

    showForm() {
        const formContainer = document.querySelector(`#${this.formContainerId}`);
        if (formContainer) {
            formContainer.classList.remove('hidden');
        }
    }

    hideForm() {
        const formContainer = this.container.querySelector(`#${this.formContainerId}`);
        if (formContainer) {
            formContainer.classList.add('hidden');
            setTimeout(() => {
                if (formContainer.classList.contains('hidden')) {
                    formContainer.innerHTML = '';
                }
            }, 350);
        }
        if (this.dataForm) {
            this.dataForm.destroy();
            this.dataForm = null;
        }
    }

    showCreateFormWithData(initialData) {
        this._showForm("edit", initialData);
    }


    onRdsUpdate(eventName, data) {
        const dataSetName = this.config.rdsSubscriptions[0];
        this.log.info(`Received real-time update for '${dataSetName}'. Refreshing data.`, data);
        this.appClient.notifier.info(`The list for '${dataSetName}' was updated in the background.`, { duration: 3000 });
        this.refreshData();
    }

    async render() {
        try {
            if (!this.initialized) {
                await this.initialize();
            }

            if (!this.container) {
                this.container = document.createElement('div');
                this.container.id = this.containerId;
                this.container.classList.add('data-crud');

                if (this.config.height === "auto") {
                    this.container.classList.add('custom-height');
                } else if (this.config.height) {
                    this.container.style.height = this.config.height;
                } else {
                    this.container.style.height = '100%';
                }

                const content = document.createElement('div');
                content.classList.add('data-crud-content');

                const viewContainer = document.createElement('div');
                viewContainer.id = this.viewContainerId;
                viewContainer.classList.add('view-container');

                const formContainer = document.createElement('div');
                formContainer.id = this.formContainerId;
                formContainer.classList.add('form-container', 'hidden');

                content.appendChild(viewContainer);
                content.appendChild(formContainer);
                this.container.appendChild(content);
            }

            const viewContainer = this.container.querySelector(`#${this.viewContainerId}`);
            viewContainer.innerHTML = '';
            viewContainer.appendChild(await this.dataView.render());

            return this.container;

        } catch (error) {
            this.handleError(error);
            return this.renderError(error);
        }
    }

    async _showForm(mode, itemData = {}) {
        if (this.dataForm) {
            this.dataForm.destroy();
            this.dataForm = null;
        }

        const formSchema = this.config.formConfig.schema || this.schema;

        this.dataForm = await this.createManagedComponent('DataForm', null, {
            appClient: this.appClient,
            eventGroup: this.eventGroup,
            instanceId: `${this.eventGroup}-form-${Date.now()}`,
            dataProvider: this.dataProvider,
            schema: formSchema,
            mode: mode,
            fields: this.config.formConfig.fields,
            sections: this.config.formConfig.sections,
            actions: {
                save: mode === 'edit',
                cancel: true
            },
            validation: this.config.formConfig.validation,
            containerClass: 'h-100',
            idField: this.config.idField,
            isDetailPane: true,
            autoSave: true,
        });

        this.dataForm.on(`dataSaved`, () => this.handleDataSaved());
        this.dataForm.on(`dataUpdated`, () => this.handleDataUpdated());
        this.dataForm.on(`cancel`, () => this.handleFormCancel());

        this.dataForm.setData(itemData);
        const formContainer = this.container.querySelector(`#${this.formContainerId}`);
        formContainer.innerHTML = '';
        formContainer.appendChild(await this.dataForm.render());

        formContainer.classList.remove('hidden');
    }



    renderError(error) {
        const container = document.createElement('div');
        container.id = this.containerId;
        container.classList.add('alert', 'alert-danger', 'm-3');
        container.innerHTML = `
            <h4 class="alert-heading">Error Loading Data</h4>
            <p>${error.message}</p>
            <hr>
            <p class="mb-0">Please try refreshing the page or contact support if the problem persists.</p>
        `;
        return container;
    }

    destroy() {
        try {
            this.dataView?.destroy();
            this.dataForm?.destroy();
            this.dataProvider?.destroy();
            this.initialized = false;
        } catch (error) {
            this.handleError(error);
        }
    }

    async loadInitialData() {
        try {
            this.log.info(`[${this.eventGroup}] Loading initial data`);
            await this.dataProvider.loadData({
                page: 1,
                pageSize: this.config.dataConfig.pageSize,
                filter: {}
            });
        } catch (error) {
            this.handleError(error);
        }
    }

    async refreshData() {
        try {
            this.log.info(`[${this.eventGroup}] Refreshing data`);
            await this.loadData({
                page: 1,
                filter: this.dataView?.getCurrentFilter() || {}
            });
        } catch (error) {
            this.handleError(error);
        }
    }

    async loadData(params = {}) {
        try {
            const loadParams = {
                page: params.page || 1,
                pageSize: params.pageSize || this.config.dataConfig.pageSize,
                filter: params.filter || {},
                sort: params.sort || {}
            };

            await this.dataProvider.loadData(loadParams);
            this.emit(`dataLoaded`, {
                data: this.dataProvider.getData(),
                totalCount: this.dataProvider.getTotalCount()
            });
        } catch (error) {
            this.handleError(error);
        }
    }

    validateConfig() {
        const required = ['eventGroup', 'rpcClass', 'schemaPath'];
        const missing = required.filter(prop => !this.config[prop]);

        if (missing.length > 0) {
            throw new Error(`Missing required configuration properties: ${missing.join(', ')}`);
        }
    }

    async loadSchema() {
        try {
            if (this.config.schema) {
                return this.config.schema;
            }
            else if (this.config.schemaPath) {
                const schemaPath = `/schemas/${this.config.schemaPath}.js`;
                const schemaModule = await import(schemaPath);

                const filename = this.config.schemaPath.split('/').pop();
                const exportName = filename.replace(/\./g, '_');

                return schemaModule[exportName] || schemaModule.default;
            }
            else {
                throw new Error("you should either set schema object or provide schemaPath");
            }

        } catch (error) {
            throw new Error(`Failed to load schema: ${error.message}`);
        }
    }


    async initializeDataProvider() {
        const referenceFields = this.extractReferenceFields();

        if (this.config.dataProviderType === 'rpc') {
            const rpcConfig = {
                autoLoad: this.config.dataConfig.autoLoad,
                appClient: this.appClient,
                eventGroup: this.eventGroup,
                rpcClass: this.config.rpcClass,
                fields: this.config.formConfig.fields,
                context: this.config.dataConfig.context,
                pageSize: this.config.dataConfig.pageSize,
                resolveReferences: this.config.dataConfig.resolveReferences,
                idField: this.config.idField,

            };

            if (this.config.rpcMethod) {
                rpcConfig.rpcMethod = this.config.rpcMethod; // List method
            }

            if (this.config.createMethod) {
                rpcConfig.createMethod = this.config.createMethod;
            }

            if (this.config.updateMethod) {
                rpcConfig.updateMethod = this.config.updateMethod;
            }

            if (this.config.deleteMethod) {
                rpcConfig.deleteMethod = this.config.deleteMethod;
            }

            if (this.config.findByIdMethod) {
                rpcConfig.findByIdMethod = this.config.findByIdMethod;
            }



            this.dataProvider = new RpcDataProvider(rpcConfig);
        } else if (this.config.dataProviderType === 'local') {
            // ... existing local provider logic ...
            this.dataProvider = new LocalDataProvider({
                appClient: this.appClient,
                eventGroup: this.eventGroup,
                fields: referenceFields,
                context: this.config.dataConfig.context,
                pageSize: this.config.dataConfig.pageSize,
                data: this.config.dataConfig.data,
                idField: this.config.idField,
            });
        } else {
            throw new Error(`Invalid dataProviderType: ${this.config.dataProviderType}`);
        }

        await this.dataProvider.initialize();
    }


    async initializeDataView() {

        const userToolbar = this.config.viewConfig.toolbar || {};

        const defaultToolbar = {
            enabled: true,
            viewModes: { enabled: true },
            create: { enabled: true },
            refresh: { enabled: true }
        };

        const mergedToolbar = {
            enabled: userToolbar.enabled ?? defaultToolbar.enabled,
            viewModes: { enabled: userToolbar.viewModes?.enabled ?? defaultToolbar.viewModes.enabled },
            create: { enabled: userToolbar.create?.enabled ?? defaultToolbar.create.enabled },
            refresh: { enabled: userToolbar.refresh?.enabled ?? defaultToolbar.refresh.enabled },
        };

        const viewConfig = {
            appClient: this.appClient,
            eventGroup: this.eventGroup,
            instanceId: this.eventGroup + '_view',
            dataProvider: this.dataProvider,

            containerClass: '',
            viewMode: this.config.viewConfig.mode,
            fields: this.config.viewConfig.fields,
            actions: this.prepareActions(),
            filter: this.config.viewConfig.filter,
            multiSelect: this.config.viewConfig.selection?.multiple || false,

            toolbar: mergedToolbar,

            autoLoad: false,
            idField: this.config.idField,
            fullHeight: false,
        };

        this.dataView = await this.createManagedComponent('DataView', null, viewConfig);
    }




    extractReferenceFields() {
        const referenceFields = {};

        if (this.config.viewConfig?.fields) {
            this.config.viewConfig.fields.forEach(field => {
                if (field?.reference) {
                    referenceFields[field.name] = { reference: { ...field.reference } };
                }
            });
        }

        if (this.config.formConfig?.fields) {
            Object.entries(this.config.formConfig.fields).forEach(([name, field]) => {
                if (field?.reference) {
                    referenceFields[name] = { reference: { ...field.reference } };
                }
            });
        }

        return referenceFields;
    }

    prepareActions() {
        const standardActions = [];

        if (!this.config.viewConfig.disableEdit) {
            standardActions.push({
                label: 'Edit',
                icon: 'bi bi-pencil',
                className: 'btn-outline-primary btn-sm',
                eventName: 'editItem'
            });
        }

        if (!this.config.viewConfig.disableDelete) {
            standardActions.push({
                label: 'Delete',
                icon: 'bi bi-trash',
                className: 'btn-outline-danger btn-sm',
                eventName: 'deleteItem'

            });
        }
        const customActionNames = new Set((this.config.viewConfig.actions || []).map(a => a.label));
        const filteredStandardActions = standardActions.filter(sa => !customActionNames.has(sa.label));

        return [...(this.config.viewConfig.actions || []), ...filteredStandardActions];
    }


    getDefaultValues() {
        const selectedProject = this.appClient.getSharedData("selectedProject");
        return {
            ...this.config.defaultValues,
            projectId: selectedProject && typeof (selectedProject === 'object') ? selectedProject._id : selectedProject
        };
    }

    handleError(error) {
        this.log.error(`[${this.eventGroup}] Error:`, error);

        this.emit(`error`, error);

        if (this.config.onError) {
            this.config.onError(error);
        }

        if (this.appClient.notifications) {
            this.appClient.notifications.show({
                type: 'error',
                message: error.message || 'An error occurred',
                duration: 5000
            });
        }
    }

    handleDataError(error) {
        this.handleError(error);
    }

    getState() {
        return {
            currentPage: this.dataProvider?.getCurrentPage() || 1,
            filter: this.dataView?.getCurrentFilter() || {},
            sort: this.dataView?.getCurrentSort() || {},
            selectedItem: this.selectedItem
        };
    }

    setContext(context) {
        this.dataProvider.setContext(context);
    }

    async setState(state) {
        if (state) {
            await this.loadData({
                page: state.page,
                filter: state.filter,
                sort: state.sort
            });

            if (state.selectedItem) {
                this.handleItemSelected(state.selectedItem);
            }
        }
    }
}